import * as THREE from 'three';
import { putMediaAsset, deleteMediaAsset, resolveMediaAsset } from './media-store.js';

// Phase 4B extends the already-cloned Sakura/Rubik media-surface capability.
// It does NOT replace Kimi's world runtime or the Property Room Studio.
// Contract remains: semantic slot -> persisted asset -> world surface -> THREE material.

const STORAGE_KEY = 'wcb.world-media-config.v1';
const SLOT_SPECS = Object.freeze({
  'world.altea.media.billboard.01': { label: 'Billboard · Paseo', shortLabel: 'Billboard', family: 'billboard', allowed: ['image','video'], fallback: 'COSTA BLANCA / WELCOME', location: 'Paseo oeste' },
  'world.altea.media.totem.01': { label: 'Totem · Orientación', shortLabel: 'Totem', family: 'totem', allowed: ['image','video'], fallback: 'EXPLORE / ALTEA', location: 'Paseo central' },
  'world.altea.media.mupi.01': { label: 'MUPI 01 · Paseo', shortLabel: 'MUPI 01', family: 'mupi', allowed: ['image','video'], fallback: 'ALTEA / DISCOVER', location: 'Paseo marítimo' },
  'world.altea.media.mupi.02': { label: 'MUPI 02 · Plaza', shortLabel: 'MUPI 02', family: 'mupi', allowed: ['image','video'], fallback: 'COSTA BLANCA / STORIES', location: 'Plaza' },
  'world.altea.media.mupi.03': { label: 'MUPI 03 · Acceso', shortLabel: 'MUPI 03', family: 'mupi', allowed: ['image','video'], fallback: 'WELCOME / WORLD', location: 'Acceso peatonal' },
  'world.altea.media.screen.01': { label: 'Pantalla digital 01 · Plaza', shortLabel: 'Pantalla 01', family: 'screen', allowed: ['image','video'], fallback: 'COSTA BLANCA / LIVE', location: 'Frente de plaza' },
  'world.altea.media.screen.02': { label: 'Pantalla digital 02 · Paseo', shortLabel: 'Pantalla 02', family: 'screen', allowed: ['image','video'], fallback: 'ALTEA / LIVE', location: 'Paseo exterior' }
});

const runtimeMedia = new Map();
const surfaces = new Map();
let config = loadConfig();
let qa = null;

function loadConfig() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
}
function saveConfig() { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); }
function slotConfig(slotId) { return config[slotId] || {}; }
function updateSlot(slotId, patch) { config = { ...config, [slotId]: { ...slotConfig(slotId), ...patch } }; saveConfig(); }

function canvasTexture(title, body) {
  const canvas = document.createElement('canvas');
  canvas.width = 1024; canvas.height = 576;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#e9dcc5'; ctx.fillRect(0,0,canvas.width,canvas.height);
  ctx.fillStyle = '#193d36'; ctx.fillRect(0,0,canvas.width,18);
  ctx.fillStyle = '#173b34'; ctx.font = '700 34px Arial'; ctx.fillText(title.toUpperCase(), 54, 90);
  ctx.fillStyle = '#3d5d55'; ctx.font = '500 22px Arial';
  const words = String(body || '').split(/\s+/); let line=''; let y=155;
  for (const word of words) { const next = `${line} ${word}`.trim(); if (ctx.measureText(next).width > 850) { ctx.fillText(line,54,y); line=word; y+=36; } else line=next; }
  if (line) ctx.fillText(line,54,y);
  ctx.fillStyle='#a36c47'; ctx.fillRect(54, canvas.height-72, 130, 5);
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; return texture;
}

function disposeSurface(slotId) {
  const prev = runtimeMedia.get(slotId);
  if (prev?.video) { prev.video.pause(); prev.video.removeAttribute('src'); prev.video.load(); }
  if (prev?.texture) prev.texture.dispose();
  runtimeMedia.delete(slotId);
}

function makeSupport(slotId, position, rotationY, size) {
  const spec = SLOT_SPECS[slotId];
  const group = new THREE.Group();
  group.name = `cbw:media:${slotId}`;
  group.userData = {
    entityId: slotId,
    entityKind: 'MEDIA_SURFACE',
    semanticRole: 'WORLD_MEDIA_SURFACE',
    slotId,
    family: spec.family,
    location: spec.location,
    supportsImage: true,
    supportsVideo: true
  };
  group.position.set(...position); group.rotation.y = rotationY;

  const frameMat = new THREE.MeshStandardMaterial({ color: 0x2e5149, roughness: .72, metalness: .04 });
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0xdccbae, roughness: .92 });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x18231f, roughness: .58, metalness: .12 });
  const [w,h] = size;
  const panel = new THREE.Mesh(new THREE.BoxGeometry(w+.18,h+.18,.12), spec.family === 'screen' ? darkMat : frameMat);
  panel.position.y = h/2 + .85; panel.castShadow = true; group.add(panel);
  const media = new THREE.Mesh(new THREE.PlaneGeometry(w,h), new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.FrontSide }));
  media.position.set(0,h/2+.85,.066); media.userData.mediaSlot = slotId; group.add(media);

  if (spec.family === 'billboard') {
    for (const x of [-w*.34,w*.34]) { const post=new THREE.Mesh(new THREE.BoxGeometry(.14,.85,.14),frameMat); post.position.set(x,.42,0); post.castShadow=true; group.add(post); }
  } else if (spec.family === 'totem') {
    const base=new THREE.Mesh(new THREE.CylinderGeometry(.42,.56,.18,16),stoneMat); base.position.y=.09; base.castShadow=true; group.add(base);
    const spine=new THREE.Mesh(new THREE.BoxGeometry(.16,.78,.16),frameMat); spine.position.y=.42; group.add(spine);
  } else if (spec.family === 'mupi') {
    const foot=new THREE.Mesh(new THREE.BoxGeometry(w*.74,.16,.52),stoneMat); foot.position.y=.08; foot.castShadow=true; group.add(foot);
    const stem=new THREE.Mesh(new THREE.BoxGeometry(.14,.72,.14),frameMat); stem.position.y=.42; group.add(stem);
  } else if (spec.family === 'screen') {
    const post=new THREE.Mesh(new THREE.BoxGeometry(.18,.8,.18),darkMat); post.position.y=.4; group.add(post);
    const foot=new THREE.Mesh(new THREE.BoxGeometry(w*.58,.14,.46),stoneMat); foot.position.y=.07; group.add(foot);
  }

  qa.scene.add(group);
  const record = { slotId, group, mesh: media, material: media.material, spec };
  surfaces.set(slotId, record);
  return record;
}

async function applySlot(slotId) {
  const surface = surfaces.get(slotId); if (!surface) return;
  disposeSurface(slotId);
  const state = slotConfig(slotId);
  if (state.assetId) {
    const asset = await resolveMediaAsset(state.assetId).catch(() => null);
    if (asset?.type === 'video') {
      const video = document.createElement('video');
      video.src=asset.url; video.muted=true; video.loop=true; video.playsInline=true; video.autoplay=true; video.preload='auto';
      const texture = new THREE.VideoTexture(video); texture.colorSpace=THREE.SRGBColorSpace; texture.minFilter=THREE.LinearFilter; texture.magFilter=THREE.LinearFilter;
      surface.material.map=texture; surface.material.needsUpdate=true; runtimeMedia.set(slotId,{video,texture}); video.play().catch(()=>{}); return;
    }
    if (asset?.type === 'image') {
      const texture = await new Promise((resolve) => new THREE.TextureLoader().load(asset.url, resolve, undefined, () => resolve(null)));
      if (texture) { texture.colorSpace=THREE.SRGBColorSpace; surface.material.map=texture; surface.material.needsUpdate=true; runtimeMedia.set(slotId,{texture}); return; }
    }
  }
  const text = state.text || surface.spec.fallback;
  const texture = canvasTexture(surface.spec.shortLabel, text); surface.material.map=texture; surface.material.needsUpdate=true; runtimeMedia.set(slotId,{texture});
}

async function setFile(slotId,file) {
  const spec=SLOT_SPECS[slotId]; if (!spec) throw new Error('Unknown media slot');
  const old = slotConfig(slotId).assetId; if (old) await deleteMediaAsset(old).catch(()=>{});
  const meta = await putMediaAsset(file);
  if (!spec.allowed.includes(meta.type)) { await deleteMediaAsset(meta.id).catch(()=>{}); throw new Error(`Unsupported asset for ${slotId}`); }
  updateSlot(slotId,{assetId:meta.id, text:''}); await applySlot(slotId); return meta;
}
async function setText(slotId,text) {
  const old=slotConfig(slotId).assetId; if(old) await deleteMediaAsset(old).catch(()=>{});
  updateSlot(slotId,{assetId:null,text:String(text||'').slice(0,240)}); await applySlot(slotId);
}
async function clearSlot(slotId) {
  const old=slotConfig(slotId).assetId; if(old) await deleteMediaAsset(old).catch(()=>{});
  config={...config,[slotId]:{}}; saveConfig(); await applySlot(slotId);
}

function buildSurfaces() {
  if (surfaces.size) return;
  // Seven bounded exterior supports. Placements are intentionally distributed
  // across promenade/plaza/access zones and remain independent from property authority.
  makeSupport('world.altea.media.billboard.01', [-13.2,0,7.2], -.08, [4.15,2.2]);
  makeSupport('world.altea.media.totem.01', [-4.2,.03,15.2], .08, [1.18,2.3]);
  makeSupport('world.altea.media.mupi.01', [-1.8,.01,7.5], -.12, [1.22,2.05]);
  makeSupport('world.altea.media.mupi.02', [6.0,.01,8.7], -.42, [1.22,2.05]);
  makeSupport('world.altea.media.mupi.03', [-10.1,.01,13.2], .18, [1.22,2.05]);
  makeSupport('world.altea.media.screen.01', [11.8,.02,12.4], -.78, [2.5,1.45]);
  makeSupport('world.altea.media.screen.02', [-15.4,.02,12.6], .18, [2.5,1.45]);
  return Promise.all([...surfaces.keys()].map(applySlot));
}

function installParentStyle() {
  if (document.getElementById('cbw-phase4-style')) return;
  const style=document.createElement('style'); style.id='cbw-phase4-style';
  style.textContent='.cbw-room-chrome{display:none!important}'; document.head.appendChild(style);
}

function commandButton(doc,label,kind) {
  const b=doc.createElement('button'); b.type='button'; b.className='iw-btn cbw-integrated-command'; b.dataset.cbwCommand=kind; b.textContent=label; return b;
}

function injectUnifiedChrome(doc) {
  const tools=doc.querySelector('.iw-topbar__tools'); if(!tools) return false;
  tools.querySelectorAll('[data-cbw-command]').forEach(n=>n.remove());
  const report=window.__CBW_SURGERY?.report?.();
  const studio=commandButton(doc, report?.mode==='STUDIO'?'View Room':'Open Studio','studio');
  studio.addEventListener('click',()=>window.__CBW_SURGERY?.studio?.());
  const exit=commandButton(doc,'← Return to Altea','exit'); exit.addEventListener('click',()=>window.__CBW_SURGERY?.exit?.());
  tools.append(studio,exit); return true;
}

function studioPanelHtml(doc) {
  const wrap=doc.createElement('section'); wrap.className='cbw-world-media-panel';
  wrap.innerHTML=`<header><div><b>CONTENIDO → EXTERIOR</b><span>Soportes personalizables del mundo · Sakura/Rubik donor</span></div><button type="button" data-cbw-media-close>×</button></header><div class="cbw-media-summary"><b>7 soportes exteriores</b><span>1 billboard · 1 totem · 3 MUPIs · 2 pantallas digitales</span></div><div class="cbw-media-scroll"></div>`;
  const scroll=wrap.querySelector('.cbw-media-scroll');
  for(const [slotId,spec] of Object.entries(SLOT_SPECS)) {
    const card=doc.createElement('article'); card.className='cbw-media-card'; card.dataset.slot=slotId;
    const persisted=slotConfig(slotId);
    card.innerHTML=`<div class="cbw-media-title"><div><b>${spec.label}</b><span>${spec.family.toUpperCase()} · ${spec.location}</span></div><i data-cbw-media-state>${persisted.assetId?'MEDIA':persisted.text?'TEXTO':'READY'}</i></div><div class="cbw-media-capability">IMAGEN / VÍDEO${spec.family==='totem'||spec.family==='mupi'?' / TEXTO':''}</div><input type="file" accept="image/png,image/jpeg,image/webp,video/mp4,video/webm" data-cbw-media-file><textarea maxlength="240" placeholder="Texto opcional / fallback del soporte" data-cbw-media-text>${persisted.text||''}</textarea><div class="cbw-media-actions"><button type="button" data-cbw-media-upload>Subir imagen / vídeo</button><button type="button" data-cbw-media-apply>Aplicar texto</button><button type="button" data-cbw-media-clear>Restaurar</button></div>`;
    const input=card.querySelector('[data-cbw-media-file]'); const state=card.querySelector('[data-cbw-media-state]');
    card.querySelector('[data-cbw-media-upload]').onclick=()=>input.click();
    input.onchange=async()=>{const file=input.files?.[0];input.value='';if(!file)return;state.textContent='APLICANDO…';try{const meta=await setFile(slotId,file);state.textContent=meta.type==='video'?'VIDEO':'IMAGEN';}catch(e){state.textContent='ERROR';}};
    card.querySelector('[data-cbw-media-apply]').onclick=async()=>{state.textContent='APLICANDO…';await setText(slotId,card.querySelector('[data-cbw-media-text]').value);state.textContent='TEXTO';};
    card.querySelector('[data-cbw-media-clear]').onclick=async()=>{await clearSlot(slotId);card.querySelector('[data-cbw-media-text]').value='';state.textContent='FALLBACK';};
    scroll.appendChild(card);
  }
  return wrap;
}

function installStudioContentEntry(doc) {
  if (doc.querySelector('[data-cbw-world-media-entry]')) return;
  const rail=doc.querySelector('.st-rail'); const list=rail?.querySelector(':scope > ul'); if(!list) return;
  // Remove the Phase 4A temporary top-level domain if an earlier guest reload left it behind.
  list.querySelector('[data-cbw-world-media-domain]')?.closest('li')?.remove();

  const contentButton=[...list.querySelectorAll('button.st-dom')].find(b=>/Contenido/i.test(b.innerText||''));
  const contentLi=contentButton?.closest('li');
  const host=contentLi || list;
  const entry=doc.createElement(contentLi?'div':'li');
  entry.className='cbw-world-media-entry';
  entry.innerHTML=`<button type="button" data-cbw-world-media-entry><span><b>Contenido exterior</b><i>Billboard · Totem · MUPIs · Pantallas</i></span><em>7</em></button>`;
  host.appendChild(entry);

  const style=doc.createElement('style'); style.dataset.cbwMediaStyle='1'; style.textContent=`
    .cbw-world-media-entry{margin:3px 0 7px}.cbw-world-media-entry>button{width:100%;display:grid;grid-template-columns:1fr auto;gap:8px;align-items:center;text-align:left;border:1px solid rgba(226,219,205,.12);border-left:2px solid #8fbf95;background:rgba(143,191,149,.035);color:var(--st-ink,#e9e3d8);padding:8px 10px;cursor:pointer}.cbw-world-media-entry b{display:block;font-size:10.5px;font-weight:500}.cbw-world-media-entry i{display:block;margin-top:2px;font-style:normal;font-size:8.5px;color:var(--st-faint,#9a968d)}.cbw-world-media-entry em{display:grid;place-items:center;width:20px;height:20px;border-radius:50%;background:rgba(143,191,149,.12);color:#a9d0ae;font-style:normal;font-size:8px}
    .cbw-world-media-panel{position:fixed;z-index:9999;right:18px;top:62px;width:min(460px,calc(100vw - 36px));max-height:calc(100vh - 82px);display:none;background:#171a18;color:#eee7dc;border:1px solid rgba(238,231,220,.18);box-shadow:0 24px 80px rgba(0,0,0,.42);font-family:Arial,sans-serif}.cbw-world-media-panel.is-open{display:block}.cbw-world-media-panel>header{display:flex;justify-content:space-between;gap:16px;align-items:center;padding:14px 16px;border-bottom:1px solid rgba(238,231,220,.12)}.cbw-world-media-panel header b{display:block;font-size:11px;letter-spacing:.12em}.cbw-world-media-panel header span{display:block;margin-top:3px;font-size:9px;color:#9da79f}.cbw-world-media-panel header button{background:none;border:0;color:#eee7dc;font-size:18px}.cbw-media-summary{display:flex;justify-content:space-between;gap:12px;padding:10px 14px;border-bottom:1px solid rgba(238,231,220,.08);font-size:9px;color:#9da79f}.cbw-media-summary b{color:#d8ded7}.cbw-media-scroll{overflow:auto;max-height:calc(100vh - 180px);padding:12px}.cbw-media-card{padding:13px;border:1px solid rgba(238,231,220,.12);margin-bottom:10px;background:rgba(255,255,255,.02)}.cbw-media-title{display:flex;justify-content:space-between;gap:10px}.cbw-media-title b{font-size:12px}.cbw-media-title span,.cbw-media-title i{display:block;font-size:8px;color:#89988f;margin-top:3px;font-style:normal}.cbw-media-capability{margin-top:8px;font-size:8px;letter-spacing:.1em;color:#b8c7bd}.cbw-media-card textarea{box-sizing:border-box;width:100%;min-height:52px;margin-top:10px;padding:9px;border:1px solid rgba(238,231,220,.15);background:#0f1210;color:#eee7dc}.cbw-media-card input{display:none}.cbw-media-actions{display:flex;gap:6px;flex-wrap:wrap;margin-top:8px}.cbw-media-actions button{border:1px solid rgba(238,231,220,.2);border-radius:999px;background:#223a34;color:#eee7dc;padding:7px 9px;font-size:9px}.cbw-media-actions button:last-child{background:transparent}
  `; doc.head.appendChild(style);
  const panel=studioPanelHtml(doc); doc.body.appendChild(panel);
  entry.querySelector('button').onclick=()=>panel.classList.add('is-open'); panel.querySelector('[data-cbw-media-close]').onclick=()=>panel.classList.remove('is-open');
}

function syncGuestIntegration() {
  const frame=document.querySelector('.cbw-room-frame'); const doc=frame?.contentDocument; if(!doc) return;
  injectUnifiedChrome(doc);
  if(window.__CBW_SURGERY?.report?.().mode==='STUDIO') installStudioContentEntry(doc);
}

function report() {
  return {
    phase:'4b-extended-world-media',
    surfaceCount:surfaces.size,
    families:Object.values(SLOT_SPECS).reduce((acc,s)=>(acc[s.family]=(acc[s.family]||0)+1,acc),{}),
    slots:Object.fromEntries(Object.keys(SLOT_SPECS).map(id=>[id,{...slotConfig(id),family:SLOT_SPECS[id].family,label:SLOT_SPECS[id].label,location:SLOT_SPECS[id].location,object:surfaces.get(id)?.group?.name||null}]))
  };
}

installParentStyle();
const wait=()=>{
  if(!window.__qa || !window.__CBW_SURGERY) return requestAnimationFrame(wait);
  qa=window.__qa;
  Promise.resolve(buildSurfaces()).then(()=>{
    window.__CBW_MEDIA={setFile,setText,clear:clearSlot,apply:applySlot,report,specs:SLOT_SPECS};
    document.documentElement.dataset.cbwPhase4='ready';
    document.documentElement.dataset.cbwPhase4b='ready';
  });
  const frame=document.querySelector('.cbw-room-frame');
  if(frame){ frame.addEventListener('load',()=>{let n=0;const tick=()=>{syncGuestIntegration();if(n++<160)setTimeout(tick,250)};tick();}); }
  setInterval(()=>{ if(window.__CBW_SURGERY?.report?.().active) syncGuestIntegration(); },750);
};
wait();
